import { useEffect, useState } from "react";
import { action, API, controller } from "api";
import { lsGet } from "utils/localStorage";

// Session cache of other outlets' answers: the switch changes rarely, and a Sales Order that
// flips between two outlets should not re-ask the server on every flip.
const policyByOutlet = new Map();

/**
 * The outlet's "Add each scan as a separate line" switch (outlet.separateCartLines).
 * Pass the result to utils/cartLine.js — `findMergeIndex(lines, key, { separateLines })` /
 * `canMergeInto(...)` — which is the only place the merge decision is made.
 *
 * No argument (the POS): the register's outlet, already in `activeReg.outlet` — the same copy
 * the POS reads allowNegativeStock and enforceMinPriceOnOpenPrice from.
 *
 * With an outlet id (Sales Invoice / Order, which may work for another branch than the open
 * register): that outlet's switch, asked once via Outlet/GetCartPolicy. Until the answer
 * arrives the screen combines, which is today's behaviour and the switch's default.
 */
const useSeparateCartLines = (outletId) => {
  const reg = lsGet("activeReg");
  const id = Number(outletId) || 0;
  const fromRegister = !id || id === Number(reg?.outletId);
  const regValue = reg?.outlet?.separateCartLines === true;

  const [fetched, setFetched] = useState(() => policyByOutlet.get(id));

  useEffect(() => {
    if (fromRegister) return;
    if (policyByOutlet.has(id)) {
      setFetched(policyByOutlet.get(id));
      return;
    }
    let live = true;
    API(controller.Outlet + action.GetCartPolicy).getById(id, (res) => {
      const value = res?.status === 200 && res.result?.separateCartLines === true;
      if (res?.status === 200) policyByOutlet.set(id, value);
      if (live) setFetched(value);
    });
    return () => {
      live = false;
    };
  }, [id, fromRegister]);

  return fromRegister ? regValue : fetched === true;
};

export default useSeparateCartLines;
